import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Eye, ExternalLink, Settings, AlertCircle, Trash2, Building2, Crown, CreditCard, Palette, BarChart3, Star, Zap, Shield, Gem, Rocket, Award, Heart, Flame, Diamond, Sparkles, BadgeCheck, CircleDollarSign, Gift, Medal, Trophy, Users } from "lucide-react";

const TIER_ICONS = { Crown, Star, Zap, Shield, Gem, Rocket, Award, Heart, Flame, Diamond, Sparkles, BadgeCheck, CircleDollarSign, Gift, Medal, Trophy };

const PRESET_COLORS = [
  "#3B82F6", "#2563EB", "#1D4ED8", "#EF4444", "#DC2626",
  "#10B981", "#059669", "#F59E0B", "#D97706", "#8B5CF6",
  "#7C3AED", "#EC4899", "#DB2777", "#06B6D4", "#0891B2",
  "#F97316", "#EA580C", "#6366F1", "#14B8A6", "#0D9488",
  "#84CC16", "#E11D48", "#000000",
];
import { getProfiles, createProfile, deleteProfile, getAppConfig, getOrganizations, getOrgProfiles } from "../services/api";
import { useTranslation } from "../i18n/LanguageContext";

const PROFILE_TYPE_COLORS = {
  business: "bg-blue-100 text-blue-700",
  professional: "bg-purple-100 text-purple-700",
  personal: "bg-green-100 text-green-700",
  organization: "bg-amber-100 text-amber-700",
};

function ProfileCard({ profile, navigate, t, onDelete }) {
  return (
    <div className="group rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-md transition overflow-hidden">
      <div className="p-6">
        <div className="flex items-start gap-4">
          {profile.profile_image ? (
            <img
              src={profile.profile_image}
              alt={profile.profile_name}
              className="h-14 w-14 rounded-full object-cover ring-2 ring-gray-100"
            />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white text-xl font-bold ring-2 ring-gray-100">
              {profile.profile_name?.charAt(0)?.toUpperCase() || "?"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {profile.profile_name}
            </h3>
            {profile.job_title && (
              <p className="mt-0.5 text-sm text-gray-500 truncate">
                {profile.job_title}
              </p>
            )}
            {!profile.job_title && profile.tagline && (
              <p className="mt-0.5 text-sm text-gray-500 truncate">
                {profile.tagline}
              </p>
            )}
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3">
          {profile.profile_type && (
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${
                PROFILE_TYPE_COLORS[profile.profile_type] ||
                "bg-gray-100 text-gray-600"
              }`}
            >
              {t(`dashboard.${profile.profile_type}`)}
            </span>
          )}
<span className="inline-flex items-center gap-1 text-xs text-gray-400">
            <Eye className="h-3.5 w-3.5" />
            {profile.views_count ?? 0} {t("dashboard.views")}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-5 border-t border-gray-100 divide-x divide-gray-100">
        <button
          onClick={() => navigate(`/app/profile/${profile.slug}`)}
          className="inline-flex flex-col items-center justify-center gap-1 py-3 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-indigo-600 transition"
          title={t("dashboard.edit")}
        >
          <Settings className="h-4 w-4" />
          {t("dashboard.edit")}
        </button>
        <button
          onClick={() => navigate(`/app/preview/${profile.slug}`)}
          className="inline-flex flex-col items-center justify-center gap-1 py-3 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-indigo-600 transition"
          title={t("dashboard.view")}
        >
          <Eye className="h-4 w-4" />
          {t("dashboard.view")}
        </button>
        <button
          onClick={() => navigate(`/app/profile/${profile.slug}/crm`)}
          className="inline-flex flex-col items-center justify-center gap-1 py-3 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-indigo-600 transition"
          title="CRM"
        >
          <Users className="h-4 w-4" />
          CRM
        </button>
        <button
          onClick={() => navigate(`/app/profile/${profile.slug}/stats`)}
          className="inline-flex flex-col items-center justify-center gap-1 py-3 text-xs font-medium text-gray-500 hover:bg-gray-50 hover:text-indigo-600 transition"
          title={t("dashboard.stats")}
        >
          <BarChart3 className="h-4 w-4" />
          {t("dashboard.stats")}
        </button>
        <button
          onClick={() => onDelete(profile)}
          className="inline-flex flex-col items-center justify-center gap-1 py-3 text-xs font-medium text-gray-400 hover:bg-red-50 hover:text-red-600 transition"
          title={t("dashboard.delete")}
        >
          <Trash2 className="h-4 w-4" />
          {t("dashboard.delete")}
        </button>
      </div>
      <div className="flex border-t border-gray-100 items-center px-4 py-2 gap-2">
        {(!profile.tier || profile.tier === "free") ? (
          <>
            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500">
              Free
            </span>
            <span className="flex-1" />
            <button
              onClick={() => navigate(`/app/upgrade?profile=${profile.slug}`)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition"
            >
              <Crown className="h-3.5 w-3.5" />
              {t("dashboard.upgrade")}
            </button>
          </>
        ) : (
          <>
            <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 capitalize">
              {(() => { const Icon = TIER_ICONS[profile.tier_icon] || Crown; return <Icon className="h-3 w-3" />; })()}
              {profile.tier}
            </span>
            {profile.subscription && (
              <span className="text-xs text-gray-400">
                {t("dashboard.subscriptionUntil")} {new Date(profile.subscription.end_date).toLocaleDateString()}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profiles, setProfiles] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [orgProfiles, setOrgProfiles] = useState({});
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [maxProfiles, setMaxProfiles] = useState(1);
  const [registeredCards, setRegisteredCards] = useState(0);
  const [maxOrgProfiles, setMaxOrgProfiles] = useState(2);
  const [form, setForm] = useState({
    profile_name: "",
    profile_type: "business",
    tagline: "",
    theme_color: "#3B82F6",
  });
  const [error, setError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [profilesRes, configRes, orgsRes] = await Promise.all([
        getProfiles(),
        getAppConfig(),
        getOrganizations(),
      ]);

      const allProfiles = profilesRes.data.profiles || [];
      setProfiles(allProfiles.filter((p) => !p.organization_id));
      setMaxProfiles(profilesRes.data.max_profiles ?? configRes.data.max_profiles);
      setRegisteredCards(profilesRes.data.registered_cards ?? 0);
      setMaxOrgProfiles(configRes.data.max_org_profiles || 2);

      const orgs = orgsRes.data.organizations || [];
      setOrganizations(orgs);

      // Fetch profiles for each org
      const orgProfilesMap = {};
      await Promise.all(
        orgs.map(async (org) => {
          try {
            const res = await getOrgProfiles(org.id);
            orgProfilesMap[org.id] = res.data.profiles || [];
          } catch {
            orgProfilesMap[org.id] = [];
          }
        })
      );
      setOrgProfiles(orgProfilesMap);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to load";
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  };

  const personalProfiles = profiles;
  const canCreateProfile = personalProfiles.length < maxProfiles;

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.profile_name.trim()) {
      setError(t("dashboard.profileName") + " is required.");
      return;
    }
    setCreating(true);
    setError("");
    try {
      const res = await createProfile(form);
      const newProfile = res.data.profile;
      navigate(`/app/preview/${newProfile.slug}`);
    } catch (err) {
      const msg = err.response?.data?.error || "Failed to create profile.";
      if (err.response?.status === 403 && err.response.data?.max_profiles) {
        setMaxProfiles(err.response.data.max_profiles);
        setShowModal(false);
      }
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const openModal = () => {
    setForm({ profile_name: "", profile_type: "business", tagline: "", theme_color: "#3B82F6" });
    setError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setError("");
  };

  const handleDelete = async (profileSlug) => {
    try {
      await deleteProfile(profileSlug);
      setProfiles((prev) => prev.filter((p) => p.slug !== profileSlug));
      // Also remove from org profiles
      setOrgProfiles((prev) => {
        const updated = { ...prev };
        for (const orgId of Object.keys(updated)) {
          updated[orgId] = updated[orgId].filter((p) => p.slug !== profileSlug);
        }
        return updated;
      });
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete profile.");
      setDeleteConfirm(null);
    }
  };

  const personalCountText = maxProfiles > 1
    ? t("dashboard.profileCountPlural", { count: personalProfiles.length, max: maxProfiles })
    : t("dashboard.profileCount", { count: personalProfiles.length, max: maxProfiles });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-indigo-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div id="dashboard-page" className="max-w-5xl">
      {/* Fetch error banner */}
      {fetchError && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-center gap-2">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {fetchError}
        </div>
      )}

      {/* Error banner from create/delete */}
      {error && !showModal && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ===== PERSONAL PROFILES SECTION ===== */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t("dashboard.title")}</h1>
            <p className="mt-1 text-sm text-gray-500">{personalCountText}</p>
          </div>
          {canCreateProfile ? (
            <button
              onClick={openModal}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 transition"
            >
              <Plus className="h-4 w-4" />
              {t("dashboard.createProfile")}
            </button>
          ) : (
            <span className="text-sm text-gray-400 bg-gray-100 rounded-lg px-4 py-2.5">
              {t("dashboard.limitReached", { count: personalProfiles.length, max: maxProfiles })}
            </span>
          )}
        </div>

        {/* Profile slots explanation */}
        <div className="mb-4 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-700 flex items-start gap-2">
          <CreditCard className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div>
            <p>
              {registeredCards > 0
                ? t("dashboard.slotsExplainWithCards", { free: 1, cards: registeredCards, total: maxProfiles })
                : t("dashboard.slotsExplainNoCards", { free: 1 })
              }
            </p>
          </div>
        </div>

        {personalProfiles.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-gray-300 bg-white p-12 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-indigo-50">
              <Plus className="h-7 w-7 text-indigo-500" />
            </div>
            <h2 className="text-lg font-semibold text-gray-800">{t("dashboard.noProfiles")}</h2>
            <p className="mt-2 text-gray-500 text-sm max-w-md mx-auto">{t("dashboard.noProfilesDesc")}</p>
            <button
              onClick={openModal}
              className="mt-5 inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 transition"
            >
              <Plus className="h-4 w-4" />
              {t("dashboard.createFirst")}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {personalProfiles.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                navigate={navigate}
                t={t}
                onDelete={setDeleteConfirm}
              />
            ))}
          </div>
        )}
      </div>

      {/* ===== ORGANIZATION SECTIONS ===== */}
      {organizations.map((org) => {
        const orgProfilesList = orgProfiles[org.id] || [];
        const orgMax = org.settings?.max_profiles || maxOrgProfiles;
        const orgCountText = orgMax > 1
          ? t("dashboard.orgProfileCountPlural", { count: orgProfilesList.length, max: orgMax })
          : t("dashboard.orgProfileCount", { count: orgProfilesList.length, max: orgMax });

        return (
          <div key={org.id} className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {org.logo_image ? (
                  <img src={org.logo_image} alt="" className="w-10 h-10 rounded-lg object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-blue-600" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{org.name}</h2>
                  <p className="text-sm text-gray-500">{orgCountText}</p>
                </div>
              </div>
              <button
                onClick={() => navigate(`/app/org/${org.id}`)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
              >
                <Settings className="h-4 w-4" />
                {t("dashboard.manageOrg")}
              </button>
            </div>

            {orgProfilesList.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
                <p className="text-sm text-gray-500">{t("dashboard.noOrgProfiles")}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {orgProfilesList.map((profile) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    navigate={navigate}
                    t={t}
                    onDelete={setDeleteConfirm}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Create Profile Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="relative w-full max-w-md rounded-2xl bg-white p-8 shadow-xl mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-6">
              {t("dashboard.createNew")}
            </h2>
            <form onSubmit={handleCreate} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("dashboard.profileName")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.profile_name}
                  onChange={(e) =>
                    setForm({ ...form, profile_name: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("dashboard.profileType")}
                </label>
                <select
                  value={form.profile_type}
                  onChange={(e) =>
                    setForm({ ...form, profile_type: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition bg-white"
                >
                  <option value="business">{t("dashboard.business")}</option>
                  <option value="professional">{t("dashboard.professional")}</option>
                  <option value="personal">{t("dashboard.personal")}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t("dashboard.tagline")}
                </label>
                <input
                  type="text"
                  value={form.tagline}
                  onChange={(e) =>
                    setForm({ ...form, tagline: e.target.value })
                  }
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("dashboard.themeColor")}
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setForm({ ...form, theme_color: color })}
                      className={`w-7 h-7 rounded-lg border-2 transition ${
                        form.theme_color === color
                          ? "border-gray-900 ring-2 ring-offset-1 ring-gray-400"
                          : "border-gray-200"
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <label className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 border border-gray-200 bg-white rounded-lg hover:bg-gray-50 transition cursor-pointer">
                    <input
                      type="color"
                      value={form.theme_color}
                      onChange={(e) => setForm({ ...form, theme_color: e.target.value })}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <Palette className="w-3.5 h-3.5" />
                    {t("dashboard.customColor")}
                  </label>
                  <div className="flex items-center border border-gray-200 rounded-lg bg-white overflow-hidden">
                    <span className="px-2 py-1.5 text-xs text-gray-400 bg-gray-50 border-r border-gray-200">#</span>
                    <input
                      type="text"
                      maxLength={6}
                      defaultValue={form.theme_color.replace("#", "")}
                      key={form.theme_color}
                      onChange={(e) => {
                        const v = e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
                        e.target.value = v;
                        if (v.length === 6) setForm((f) => ({ ...f, theme_color: `#${v}` }));
                      }}
                      className="w-[5.5rem] px-2 py-1.5 text-sm font-mono text-gray-700 border-none outline-none uppercase"
                      placeholder="3B82F6"
                    />
                  </div>
                  <span
                    className="w-8 h-8 rounded-lg border border-black/10 flex-shrink-0"
                    style={{ backgroundColor: form.theme_color }}
                  />
                </div>
              </div>
              {error && (
                <p className="text-sm text-red-600 bg-red-50 rounded-lg px-4 py-2">
                  {error}
                </p>
              )}
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
                >
                  {t("dashboard.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-50 transition"
                >
                  {creating ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      {t("dashboard.creating")}
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      {t("dashboard.createProfile")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDeleteConfirm(null)}
          />
          <div className="relative w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl mx-4 text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">{t("dashboard.deleteProfile")}</h3>
            <p className="mt-2 text-sm text-gray-500">
              {t("dashboard.deleteConfirm")} <strong>{deleteConfirm.profile_name}</strong>? {t("dashboard.deleteWarn")}
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded-lg px-5 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
              >
                {t("dashboard.cancel")}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm.slug)}
                className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-red-700 transition"
              >
                {t("dashboard.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
